import type React from "react";
import { DealMainWorkflowVendorSub } from "~/components/common/deals/show/main/DealMainWorkflowVendorSub";
import { PartnerAssignmentResponseForm } from "~/components/common/deals/show/main/PartnerAssignmentResponseForm";
import { StatusBar } from "~/components/common/parts/status/StatusBar";
import { StatusReasonCard } from "~/components/common/statuses/StatusReasonCard";
import { StatusSwitcher } from "~/components/specifics/vendors/deals/[id]/statuses/StatusSwitcher";
import { DealContext } from "~/context/DealContext";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import { useStatuses } from "~/hooks/vendors/useStatuses";
import {
	getOriginVendor,
	getSelfDealVendor,
	getSubVendor,
	isOriginVendor
} from "~/models/DealVendorCompany";
import {
	getWithVendorStatuses,
	isCancelApplying,
	isCanceled,
	isCompleted,
	NOW_ACCEPTING
} from "~/models/Status";
import Styles from "./DealMainWorkflow.module.scss";
import { DealMainWorkflowVendorCancel } from "./DealMainWorkflowVendorCancel";

export const DealMainWorkflowVendor: React.FC = () => {
	const id = useIdQueryHandler();
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const { data: statuses } = useStatuses().index();
	const { data: session } = useSessions().index();
	if (deal === null || session === null || statuses === null) {
		return null;
	}

	const isSubVendor = !isOriginVendor(deal, session.vendorCompany.id);
	const selfDealVendor = getSelfDealVendor(deal, session.vendorCompany.id);
	const subVendor = getSubVendor(deal);
	const isPendingAssignment =
		selfDealVendor?.partnerAssignmentResponseStatus === "pending";
	const canPartnerRespond =
		isSubVendor &&
		isPendingAssignment &&
		!isCompleted(deal.statusId) &&
		!isCanceled(deal.statusId);

	return (
		<DealContext.Provider value={{ deal, mutateDeal }}>
			{isCancelApplying(deal) ? (
				<>
					<DealMainWorkflowVendorCancel isSubVendor={isSubVendor} />
					{/* 案件取り下げ申請中は、実施会社アサイン取り下げだけできるようにする */}
					{!isSubVendor && subVendor !== null ? (
						<DealMainWorkflowVendorSub />
					) : null}
				</>
			) : (
				<>
					{!isCanceled(deal.statusId) && (
						<>
							<div className={Styles.StatusCard}>
								<h2 className={Styles.Header}>
									{deal.companySetting.name}からの依頼
								</h2>
								{isSubVendor ? (
									<h4 className={Styles.SubVendorLabel}>
										施工会社：{getOriginVendor(deal)?.name}
									</h4>
								) : null}
								<StatusBar
									currentId={deal.statusId}
									statuses={getWithVendorStatuses(statuses)}
								/>
							</div>
							{deal.statusId !== NOW_ACCEPTING && <DealMainWorkflowVendorSub />}
						</>
					)}
					<StatusReasonCard deal={deal} />
					<div className={Styles.WorkflowCard}>
						{canPartnerRespond ? (
							<PartnerAssignmentResponseForm />
						) : (
							<StatusSwitcher statusId={deal.statusId} />
						)}
					</div>
				</>
			)}
		</DealContext.Provider>
	);
};
